import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Search, Trash2, AlertTriangle, CheckSquare, Square, X, Info,
} from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { productImageUrl } from '@/lib/imageUrl';

// Default cutoff = today (local ISO YYYY-MM-DD)
const today = () => new Date().toISOString().slice(0, 10);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

export default function AdminSoldCleanup() {
  const { logout } = useAuth() || {};
  const [cutoff, setCutoff] = useState(today());
  const [items, setItems] = useState(null); // null = never searched
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  const runSearch = async () => {
    if (!cutoff) {
      toast.error('Please choose a cut-off date');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/admin/products/sold-before', { params: { date: cutoff } });
      setItems(data.items || []);
      setSelected(new Set());
      setLastReport(null);
      if ((data.items || []).length === 0) {
        toast.info(`No products marked sold on or before ${fmtDate(cutoff + 'T23:59:59')}`);
      }
    } catch (e) {
      toast.error(formatApiError(e, 'Search failed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const allSelected = useMemo(
    () => (items && items.length > 0 && items.every((it) => selected.has(it.slug))),
    [items, selected]
  );
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set((items || []).map((it) => it.slug)));
  };

  const openConfirm = () => {
    if (selected.size === 0) {
      toast.error('Pick at least one product first');
      return;
    }
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const slugs = [...selected];
      const { data } = await api.post('/admin/products/bulk-delete', { slugs, date: cutoff });
      setLastReport(data);
      toast.success(
        data.deleted === slugs.length
          ? `Permanently deleted ${data.deleted} product${data.deleted === 1 ? '' : 's'}`
          : `Deleted ${data.deleted} of ${slugs.length}. ${data.skipped_slugs.length} skipped — likely un-marked since search.`
      );
      // Remove deleted rows from the current list
      const deletedSet = new Set(data.deleted_slugs || []);
      setItems((prev) => (prev || []).filter((it) => !deletedSet.has(it.slug)));
      setSelected(new Set());
      setConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiError(e, 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="min-h-screen bg-ivory text-charcoal" data-testid="sold-cleanup-root">
      <header className="border-b border-brandborder bg-white/90 sticky top-0 z-30 backdrop-blur">
        <div className="container-luxe py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon transition-colors">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
            <div className="hidden md:block h-4 w-px bg-brandborder" />
            <div>
              <div className="overline text-charcoal/60">Inventory Housekeeping</div>
              <div className="font-display text-lg text-maroon">Sold Products Cleanup</div>
            </div>
          </div>
          <button onClick={logout} className="text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon transition-colors">
            Sign out
          </button>
        </div>
      </header>

      <section className="container-luxe py-8 lg:py-10">
        <p className="text-sm text-charcoal/70 max-w-2xl mb-6">
          Permanently remove products that have been sold. Pick a cut-off date, review what matches,
          then delete the ones you no longer want in the catalog. Deletion is irreversible.
        </p>

        {/* ------ Search bar ------ */}
        <div className="border border-brandborder bg-white p-5 lg:p-6 mb-6" data-testid="search-controls">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="overline block mb-2" htmlFor="cutoff-date">
                Show products marked Sold on or before
              </label>
              <input
                id="cutoff-date"
                type="date"
                value={cutoff}
                onChange={(e) => setCutoff(e.target.value)}
                max={today()}
                data-testid="cutoff-date"
                className="w-full md:w-64 px-3 py-2.5 border border-brandborder focus:border-maroon focus:outline-none text-charcoal bg-white text-sm"
              />
            </div>
            <button
              onClick={runSearch}
              disabled={loading}
              data-testid="search-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-maroon text-ivory hover:bg-maroon/90 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
          <div className="mt-4 flex items-start gap-2 text-xs text-charcoal/55">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Products marked sold before this feature launched are matched by their last-updated date; newly sold products use the exact sold-on timestamp.</span>
          </div>
        </div>

        {/* ------ Results ------ */}
        {items !== null && (
          <div className="border border-brandborder bg-white" data-testid="results-panel">
            <div className="px-5 lg:px-6 py-4 border-b border-brandborder flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-charcoal">
                <span className="font-medium">{items.length}</span> product{items.length === 1 ? '' : 's'} marked sold on or before {fmtDate(cutoff + 'T23:59:59')}
                {selected.size > 0 && <span className="text-charcoal/60"> · {selected.size} selected</span>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleAll}
                  disabled={items.length === 0}
                  data-testid="select-all-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {allSelected ? <CheckSquare className="w-4 h-4" /> : someSelected ? <span className="w-4 h-4 border-2 border-current bg-current/20" /> : <Square className="w-4 h-4" />}
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={openConfirm}
                  disabled={selected.size === 0}
                  data-testid="delete-selected-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-[11px] uppercase tracking-[0.18em] transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected Permanently{selected.size > 0 ? ` (${selected.size})` : ''}
                </button>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="p-10 text-center text-sm text-charcoal/60" data-testid="empty-state">
                Nothing matches this cut-off date. Try a later date, or head back to the admin dashboard.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="results-table">
                  <thead className="bg-ivory/40 border-b border-brandborder">
                    <tr className="text-left overline text-charcoal/60">
                      <th className="px-4 py-3 w-10"></th>
                      <th className="px-2 py-3 w-16"></th>
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3 hidden md:table-cell">Category</th>
                      <th className="px-4 py-3">Sold On</th>
                      <th className="px-4 py-3 hidden md:table-cell">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const isSel = selected.has(it.slug);
                      const img = productImageUrl(it);
                      const soldDisplay = it.sold_at || it.effective_sold_at || it.updated_at;
                      const legacy = !it.sold_at && it.effective_sold_at;
                      return (
                        <tr
                          key={it.slug}
                          onClick={() => toggleOne(it.slug)}
                          className={`border-b border-brandborder/60 cursor-pointer transition-colors ${isSel ? 'bg-maroon/5' : 'hover:bg-ivory/40'}`}
                          data-testid={`row-${it.slug}`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleOne(it.slug)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`check-${it.slug}`}
                              className="w-4 h-4 accent-maroon"
                              aria-label={`Select ${it.label}`}
                            />
                          </td>
                          <td className="px-2 py-3">
                            {img ? (
                              <img src={img} alt="" className="w-12 h-14 object-cover border border-brandborder/60" />
                            ) : (
                              <div className="w-12 h-14 bg-ivory border border-brandborder/60" />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-charcoal">{it.label}</div>
                            <div className="text-[11px] text-charcoal/50 mt-0.5">/{it.slug}</div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-charcoal/70">{it.category}</td>
                          <td className="px-4 py-3">
                            <div className="text-charcoal">{fmtDate(soldDisplay)}</div>
                            {legacy && (
                              <div className="text-[10px] uppercase tracking-[0.14em] text-charcoal/40 mt-0.5">from update time</div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-charcoal/70">{it.price || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {lastReport && lastReport.skipped_slugs && lastReport.skipped_slugs.length > 0 && (
          <div className="mt-4 border border-amber-300 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="skipped-report">
            <div className="font-medium mb-1">{lastReport.skipped_slugs.length} product(s) were skipped</div>
            <div className="text-xs text-amber-800/80">
              They were no longer marked sold or fell outside the cut-off when we double-checked on the server. They remain in your catalog.
            </div>
          </div>
        )}
      </section>

      {/* ------ Confirmation modal ------ */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm"
          onClick={() => !deleting && setConfirmOpen(false)}
          data-testid="confirm-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white border border-brandborder shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-brandborder flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-700" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-charcoal">Delete {selected.size} product{selected.size === 1 ? '' : 's'} permanently?</div>
                <div className="text-sm text-charcoal/70 mt-1">
                  This cannot be undone. The product data, images, and SEO URLs will be removed from your site immediately.
                </div>
              </div>
              <button
                onClick={() => !deleting && setConfirmOpen(false)}
                aria-label="Close"
                className="text-charcoal/60 hover:text-charcoal p-1 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 bg-ivory/40 max-h-56 overflow-y-auto">
              <ul className="text-sm space-y-1.5">
                {[...selected].slice(0, 20).map((slug) => {
                  const it = (items || []).find((i) => i.slug === slug);
                  return (
                    <li key={slug} className="flex items-baseline gap-2">
                      <span className="w-1 h-1 rounded-full bg-maroon shrink-0" />
                      <span className="truncate">{it?.label || slug}</span>
                    </li>
                  );
                })}
                {selected.size > 20 && (
                  <li className="text-xs text-charcoal/50 pt-1">+ {selected.size - 20} more</li>
                )}
              </ul>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                data-testid="cancel-delete-btn"
                className="px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                data-testid="confirm-delete-btn"
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-700 hover:bg-red-800 text-white text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-60"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting…' : 'Yes, delete permanently'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
